// The explorer's "Post a grade" tab: sends one student's MyEducator grade on an activity to Canvas
// through CanvasConnectClient.api_post_grade_for_user (links/lib/lms_client/canvas_explorer_grades.py).
// The student's Canvas user ID is looked up from Canvas on every post.

import React, { Fragment, useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import { Button } from '@CommonComponents/buttons'
import { toast } from '@CommonComponents/toast'
import { client } from '@Utils'
import { canvasOrgKeyManagement as endpoints } from '@URLS'
import { CodeBlock } from './ResultView'

const json = value => JSON.stringify(value, null, 2)

const FORM_ID = 'post-grade-form'

const errorFrom = err => {
  const data = (err.response && err.response.data) || {}
  return {
    message: data.error || err.message || 'The request failed.',
    canvasStatus: data.canvasStatus,
    canvasBody: data.canvasBody,
  }
}

export const studentLabel = student => {
  const name = [student.lastName, student.firstName].filter(Boolean).join(', ')
  return name ? `${name} (${student.email})` : student.email
}

export const activityLabel = activity => {
  const points =
    activity.points === null || activity.points === undefined
      ? ''
      : ` · ${activity.points} pts`
  const unmapped = activity.canvasAssignmentId ? '' : ' · no Canvas ID'
  return `${activity.title}${points}${unmapped}`
}

// activities grouped under their activity group, in the order the server sent them
export const groupActivities = activities =>
  activities.reduce((groups, activity) => {
    const group = groups.find(g => g.name === activity.group)
    if (group) group.activities.push(activity)
    else groups.push({ name: activity.group, activities: [activity] })
    return groups
  }, [])

const AssignmentIdForm = ({ contextId, activity, onSaved }) => {
  const [draft, setDraft] = useState('')
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState(null)
  const save = e => {
    e.preventDefault()
    setSaving(true)
    setError(null)
    client
      .post(endpoints.saveActivityCanvasAssignmentId({ contextId }), {
        activityId: activity.id,
        canvasAssignmentId: draft.trim(),
      })
      .then(resp => {
        toast.success('Canvas assignment ID saved')
        onSaved(resp.data.canvasAssignmentId)
      })
      .catch(err => setError(errorFrom(err).message))
      .finally(() => setSaving(false))
  }
  return (
    <form onSubmit={save} className="mt-2">
      <p className="text-muted mb-2" id="post-grade-assignment-help">
        This activity isn&apos;t mapped to a Canvas assignment, so its grade has
        nowhere to go. Enter the assignment&apos;s ID (the number after
        /assignments/ in its Canvas URL) and save it.
      </p>
      <div className="form-group">
        <label htmlFor="post-grade-assignment-id">Canvas assignment ID</label>
        <input
          id="post-grade-assignment-id"
          className="form-control"
          type="text"
          inputMode="numeric"
          pattern="[1-9][0-9]*"
          required
          aria-describedby="post-grade-assignment-help"
          aria-invalid={error ? 'true' : undefined}
          value={draft}
          onChange={e => setDraft(e.target.value)}
        />
        {error && (
          <small className="form-text text-danger" role="alert">
            {error}
          </small>
        )}
      </div>
      <Button
        type="submit"
        className="me-btn me-btn-secondary me-btn-sm"
        disabled={saving}
      >
        {saving ? 'Saving…' : 'Save assignment ID'}
      </Button>
    </form>
  )
}

AssignmentIdForm.propTypes = {
  contextId: PropTypes.string.isRequired,
  activity: PropTypes.object.isRequired,
  onSaved: PropTypes.func.isRequired,
}

const PostGrade = ({ contextId, canPost }) => {
  const [options, setOptions] = useState(null)
  const [loadError, setLoadError] = useState(null)
  const [coursenum, setCoursenum] = useState('')
  const [studentId, setStudentId] = useState('')
  const [activityId, setActivityId] = useState('')
  const [overrideLateStatus, setOverrideLateStatus] = useState(true)
  const [fetchPointsFromApi, setFetchPointsFromApi] = useState(true)
  const [alternateTotalPoints, setAlternateTotalPoints] = useState('')
  const [posting, setPosting] = useState(false)
  const [result, setResult] = useState(null)

  const loadOptions = course => {
    setLoadError(null)
    client
      .get(endpoints.getPostGradeOptions({ contextId, coursenum: course }))
      .then(resp => {
        setOptions(resp.data)
        setCoursenum(resp.data.coursenum || '')
        setStudentId('')
        setActivityId('')
        // a result from the previous course would read as if it were for this one
        setResult(null)
      })
      .catch(err => setLoadError(errorFrom(err).message))
  }

  useEffect(() => loadOptions(null), [contextId])

  const student = options && options.students.find(s => s.id === studentId)
  const activity = options && options.activities.find(a => a.id === activityId)

  const setActivityCanvasId = canvasAssignmentId =>
    setOptions({
      ...options,
      activities: options.activities.map(a =>
        a.id === activityId ? { ...a, canvasAssignmentId } : a
      ),
    })

  const submit = e => {
    e.preventDefault()
    setPosting(true)
    setResult(null)
    client
      .post(endpoints.postGradeForUser({ contextId }), {
        userId: studentId,
        activityId,
        overrideLateStatus,
        fetchPointsFromApi,
        alternateTotalPoints: fetchPointsFromApi
          ? ''
          : alternateTotalPoints.trim(),
      })
      .then(resp => {
        toast.success('Grade posted to Canvas')
        setResult({ ok: true, student, activity, data: resp.data.data })
      })
      .catch(err =>
        setResult({ ok: false, student, activity, error: errorFrom(err) })
      )
      .finally(() => setPosting(false))
  }

  if (loadError) {
    return (
      <section className="me-card" aria-labelledby="post-grade-title">
        <header className="me-card-header">
          <div className="me-card-heading">
            <h2 id="post-grade-title" className="me-card-title">
              Post a grade
            </h2>
          </div>
        </header>
        <div className="me-card-alert me-card-alert-danger" role="alert">
          <i
            className="fa-regular fa-triangle-exclamation"
            aria-hidden="true"
          />
          <span>{loadError}</span>
        </div>
      </section>
    )
  }
  if (!options) return <p>Loading ...</p>

  const ready =
    canPost && student && activity && activity.canvasAssignmentId && !posting

  return (
    <Fragment>
      <section className="me-card" aria-labelledby="post-grade-title">
        <header className="me-card-header">
          <div className="me-card-heading">
            <h2 id="post-grade-title" className="me-card-title">
              Post a grade
            </h2>
            <div className="me-card-meta">
              <span>
                Sends a student&apos;s MyEducator grade on one activity to
                Canvas
              </span>
              <code>api_post_grade_for_user</code>
            </div>
          </div>
        </header>
        {!options.courses.length && (
          <div className="me-card-alert me-card-alert-warning" role="note">
            <i
              className="fa-regular fa-triangle-exclamation"
              aria-hidden="true"
            />
            <span>No MyEducator course is connected to this context.</span>
          </div>
        )}
        {options.courses.length > 0 && (
          <Fragment>
            <div className="me-card-section">
              <div className="me-card-section-header">
                <h3 className="me-card-section-title">Student and activity</h3>
              </div>
              {options.courses.length > 1 && (
                <div className="form-group">
                  <label htmlFor="post-grade-course">Course</label>
                  <select
                    id="post-grade-course"
                    form={FORM_ID}
                    className="form-control"
                    value={coursenum}
                    onChange={e => loadOptions(e.target.value)}
                  >
                    {options.courses.map(course => (
                      <option key={course.coursenum} value={course.coursenum}>
                        {course.title} ({course.coursenum})
                      </option>
                    ))}
                  </select>
                </div>
              )}
              <div className="form-group">
                <label htmlFor="post-grade-student">
                  Student{' '}
                  <span className="text-muted">
                    ({options.students.length})
                  </span>
                </label>
                <select
                  id="post-grade-student"
                  form={FORM_ID}
                  className="form-control"
                  required
                  value={studentId}
                  onChange={e => setStudentId(e.target.value)}
                >
                  <option value="">Choose a student…</option>
                  {options.students.map(s => (
                    <option key={s.id} value={s.id}>
                      {studentLabel(s)}
                    </option>
                  ))}
                </select>
              </div>
              <div className="form-group mb-0">
                <label htmlFor="post-grade-activity">Assignment</label>
                <select
                  id="post-grade-activity"
                  form={FORM_ID}
                  className="form-control"
                  required
                  value={activityId}
                  onChange={e => setActivityId(e.target.value)}
                >
                  <option value="">Choose an activity…</option>
                  {groupActivities(options.activities).map(group => (
                    <optgroup key={group.name} label={group.name}>
                      {group.activities.map(a => (
                        <option key={a.id} value={a.id}>
                          {activityLabel(a)}
                        </option>
                      ))}
                    </optgroup>
                  ))}
                </select>
                {activity && activity.canvasAssignmentId && (
                  <small className="form-text text-muted">
                    Canvas assignment {activity.canvasAssignmentId}
                  </small>
                )}
              </div>
              {activity && !activity.canvasAssignmentId && (
                <AssignmentIdForm
                  key={activity.id}
                  contextId={contextId}
                  activity={activity}
                  onSaved={setActivityCanvasId}
                />
              )}
            </div>

            <div className="me-card-section">
              <div className="me-card-section-header">
                <h3 className="me-card-section-title">Options</h3>
              </div>
              <div className="form-check mb-3">
                <input
                  id="post-grade-override-late"
                  form={FORM_ID}
                  className="form-check-input"
                  type="checkbox"
                  aria-describedby="post-grade-override-late-help"
                  checked={overrideLateStatus}
                  onChange={e => setOverrideLateStatus(e.target.checked)}
                />
                <label
                  className="form-check-label"
                  htmlFor="post-grade-override-late"
                >
                  Override late status
                </label>
                <small
                  id="post-grade-override-late-help"
                  className="form-text text-muted"
                >
                  Sets the submission&apos;s late policy status to
                  &quot;none&quot; so Canvas doesn&apos;t flag it late or apply
                  its late penalty.
                </small>
              </div>
              <div className="form-check mb-3">
                <input
                  id="post-grade-fetch-points"
                  form={FORM_ID}
                  className="form-check-input"
                  type="checkbox"
                  aria-describedby="post-grade-fetch-points-help"
                  checked={fetchPointsFromApi}
                  onChange={e => setFetchPointsFromApi(e.target.checked)}
                />
                <label
                  className="form-check-label"
                  htmlFor="post-grade-fetch-points"
                >
                  Scale to the Canvas assignment&apos;s points possible
                </label>
                <small
                  id="post-grade-fetch-points-help"
                  className="form-text text-muted"
                >
                  Reads points possible from Canvas and scales the grade to it.
                  Turn this off to enter points possible yourself, or to send
                  the MyEducator points unscaled.
                </small>
              </div>
              <div className="form-group mb-0">
                <label htmlFor="post-grade-total-points">
                  Canvas points possible (optional)
                </label>
                <input
                  id="post-grade-total-points"
                  form={FORM_ID}
                  className="form-control"
                  type="number"
                  min="0"
                  step="any"
                  aria-describedby="post-grade-total-points-help"
                  disabled={fetchPointsFromApi}
                  value={fetchPointsFromApi ? '' : alternateTotalPoints}
                  onChange={e => setAlternateTotalPoints(e.target.value)}
                />
                <small
                  id="post-grade-total-points-help"
                  className="form-text text-muted"
                >
                  {fetchPointsFromApi
                    ? 'Read from Canvas while scaling is on.'
                    : 'Scales the grade from the activity’s points to this total. Leave blank to send the MyEducator points as they are.'}
                </small>
              </div>
            </div>

            {/* the fields above join this form through their form attribute, so the sections
                stay direct children of the card and keep its dividers */}
            <form id={FORM_ID} className="me-card-section" onSubmit={submit}>
              <Button
                type="submit"
                className="me-btn me-btn-primary"
                disabled={!ready}
              >
                {posting ? 'Posting…' : 'Post grade to Canvas'}
              </Button>
              {!canPost && (
                <small className="form-text text-muted">
                  This context has no Canvas course ID, so a grade can&apos;t be
                  posted.
                </small>
              )}
            </form>
          </Fragment>
        )}
      </section>

      {result && (
        <section
          className="me-card"
          aria-labelledby="post-grade-result-title"
          aria-live="polite"
        >
          <header className="me-card-header">
            <div className="me-card-heading">
              <span className="me-card-eyebrow">Result</span>
              <h2 id="post-grade-result-title" className="me-card-title">
                {result.ok ? 'Grade posted' : 'Grade not posted'}
              </h2>
              <div className="me-card-meta">
                {result.student && <span>{studentLabel(result.student)}</span>}
                {result.activity && <span>{result.activity.title}</span>}
                {result.ok && (
                  <span>Canvas user {result.data.canvasUserId}</span>
                )}
              </div>
            </div>
            <div className="me-card-header-end">
              <div className="me-card-status">
                {result.ok ? (
                  <span className="me-badge-tint me-badge-tint-success">
                    <i
                      className="fa-regular fa-check me-badge-ico"
                      aria-hidden="true"
                    />
                    Sent
                  </span>
                ) : (
                  <span className="me-badge-tint me-badge-tint-danger">
                    <i
                      className="fa-regular fa-xmark me-badge-ico"
                      aria-hidden="true"
                    />
                    Failed
                  </span>
                )}
              </div>
            </div>
          </header>
          {!result.ok && (
            <div className="me-card-alert me-card-alert-danger" role="alert">
              <i
                className="fa-regular fa-triangle-exclamation"
                aria-hidden="true"
              />
              <span>{result.error.message}</span>
            </div>
          )}
          {(result.ok || result.error.canvasBody !== undefined) && (
            <div className="me-card-body">
              {result.ok ? (
                <CodeBlock
                  label="Canvas submission"
                  text={json(result.data.submission)}
                />
              ) : (
                <CodeBlock
                  label={`Canvas response (${result.error.canvasStatus})`}
                  text={
                    typeof result.error.canvasBody === 'string'
                      ? result.error.canvasBody
                      : json(result.error.canvasBody)
                  }
                />
              )}
            </div>
          )}
        </section>
      )}
    </Fragment>
  )
}

PostGrade.propTypes = {
  contextId: PropTypes.string.isRequired,
  // false when the context has no Canvas course ID
  canPost: PropTypes.bool.isRequired,
}

export default PostGrade
